import './SegmentationLabeling.scss';
import cn from 'classnames';
import downloadjs from 'downloadjs';
import { gzip } from 'pako';
import Papa from 'papaparse';
import * as R from 'ramda';
import { Dispatch, useCallback, useEffect, useMemo, useState } from 'react';
import { Nav, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { BiBookmarkHeart } from 'react-icons/bi';
import { MdCheck, MdDelete, MdNewReleases, MdOutlineFileDownload, MdSend } from 'react-icons/md';
import { formatNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import type { Option } from '@blisspointmedia/bpm-types/dist/FilterBar';
import {
  applyRuleUpdatesToEditsMap,
  BILLING_SEGMENT_NAMES,
  buildSegmentEditContext,
  buildUpdatedBillingRowsPayload,
  buildUpdatedRowsPayload,
  GroupedSegment,
  isBillingSegmentName,
  prepareUpdatesForRules,
  RawBillingSegmentMapEntry,
  SegmentationMappingLabelRule
} from '@blisspointmedia/bpm-types/dist/SegmentationMapping';
import { AlertDialog } from '@Components/AlertDialog';
import { Button } from '@Components/Button';
import { Appearance, Intent, IntentType, Placement, Size } from '@Components/designConstants';
import { IconButton } from '@Components/IconButton';
import { PendingChangesControls, PendingChangesPanel } from '@Components/PendingChanges';
import { RowType } from '@Components/PendingChanges/pending-changes-utils';
import { FullPageSpinner } from '@Components/Spinner';
import { Header } from '@Components/StickyTable';
import { FilterBar } from '@src/Components/FilterBar';
import { useAppSelector } from '../../redux';
import { useSetError } from '../../redux/modals';
import * as UserRedux from '../../redux/user';
import { Brand60, Warning500 } from '../../utils/colors';
import { DateRange } from '../../utils/date-utils';
import { db } from '../../utils/db';
import {
  awaitJSON,
  CrossChannelLambdaFetch,
  S3Put,
  SegmentationMappingLambdaFetch
} from '../../utils/fetch-utils';
import { useExperimentFlag } from '../../utils/experiment-context';
import { useStateFunction } from '../../utils/hooks/useData';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { Mixpanel, MxE } from '../../utils/mixpanelWrapper';
import {
  BillingMappingLabelRule,
  CustomSegmentsData,
  EditsMapAction,
  SegmentEditsMap,
  SegmentRow,
  SegmentRowEdit,
  TabOptions,
  TabOptionsType
} from './segmentation-mapping-constants';
import { MixedBillingSegmentModal } from './MixedBillingSegmentModal';
import { PendingSegmentationEdits } from './PendingSegmentationEdits';
import { resolveBillingSegmentValue, SegmentationLabelingTable } from './SegmentationLabelingTable';
import { formatDateRange, useGroupedSegments } from './segmentationMappingUtils';
import {
  ModifiedBillingMappingLabelRule,
  ModifiedSegmentationMappingLabelRule,
  SelectLabelingRulesModal
} from './SelectLabelingRulesModal';

const S3_BUCKET_LOCATION = 'bpm-cdn';

/**
 * Download segmentation data as CSV with proper Unicode/emoji handling
 */
const downloadSegmentationDataToCSV = (data: Record<string, unknown>[], fileName: string): void => {
  if (!(data && fileName)) {
    console.error('Must provide data and fileName to download segmentation data to CSV');
    return;
  }

  const csv = Papa.unparse(data, {
    quotes: true,
    quoteChar: '"',
    escapeChar: '"',
    delimiter: ',',
    header: true,
    newline: '\n',
    skipEmptyLines: false,
    columns: undefined
  });

  const BOM = '﻿';
  const csvWithBOM = BOM + csv;

  downloadjs(csvWithBOM, `${fileName}.csv`, 'text/csv;charset=utf-8');
};

// Renders every billing segment entry (sorted by start date) as
// "Value (start — end); Value (start — end)" for CSV export.
const formatBillingSegmentEntries = (
  entries: RawBillingSegmentMapEntry[],
  segmentName: string
): string =>
  [...entries]
    .sort((a, b) => a.start_date.localeCompare(b.start_date))
    .map(entry => `${entry[segmentName]} (${formatDateRange(entry.start_date, entry.end_date)})`)
    .join('; ');

interface SegmentationLabelingProps {
  data: SegmentRow[] | null;
  customSegments: CustomSegmentsData[] | undefined;
  fetchRawData: () => Promise<void>;
  dataGranularity: 'ad' | 'ad_group' | 'campaign';
  labelingRules?: SegmentationMappingLabelRule[];
  billingLabelingRules?: BillingMappingLabelRule[];
}

const filterEditsByCompleteness = (
  editsMap: SegmentEditsMap,
  segmentNames: string[],
  keepComplete: boolean
): SegmentEditsMap => {
  const isSegmentSet = (value: SegmentRow, segmentName: string): boolean => {
    const segmentValue = value.edits?.[segmentName] ?? value[segmentName];
    return Array.isArray(segmentValue) ? segmentValue.length > 0 : Boolean(segmentValue);
  };

  const result = { ...editsMap };
  for (const key of Object.keys(result)) {
    const value = result[key];
    const hasAllSegments = segmentNames.every(segmentName => isSegmentSet(value, segmentName));
    if (keepComplete ? !hasAllSegments : hasAllSegments) {
      delete result[key];
    }
  }
  return result;
};

interface PendingSegmentRow extends RowType {
  id: string;
  campaignKey: string;
  segmentRow: SegmentRow;
  dispatch: Dispatch<EditsMapAction>;
  hideUnchanged: boolean;
}

const formatSegmentRows = (
  editsMap: SegmentEditsMap,
  dispatch: Dispatch<EditsMapAction>,
  hideUnchanged: boolean
): PendingSegmentRow[] =>
  Object.entries(editsMap).map(([campaignKey, segmentRow]) => ({
    id: campaignKey,
    campaignKey,
    segmentRow,
    dispatch,
    hideUnchanged
  }));

const buildSegmentHeaders = (
  segmentNames: string[],
  groupedSegments: Record<string, GroupedSegment[]>
): Header[] => [
  {
    field: 'id',
    label: '',
    renderer: (row: RowType) => {
      const segmentPendingRow = row as PendingSegmentRow;
      return (
        <PendingSegmentationEdits
          campaign_key={segmentPendingRow.campaignKey}
          editsMap={{ [segmentPendingRow.campaignKey]: segmentPendingRow.segmentRow }}
          dispatchEditsMap={segmentPendingRow.dispatch}
          segmentNames={segmentNames}
          groupedSegments={groupedSegments}
          hideUnchanged={segmentPendingRow.hideUnchanged}
        />
      );
    }
  }
];

export const SegmentationLabeling = ({
  data,
  customSegments,
  fetchRawData,
  dataGranularity,
  labelingRules = [],
  billingLabelingRules = []
}: SegmentationLabelingProps) => {
  const { cid } = useCompanyInfo();
  const setError = useSetError();

  const [selectedRows, setSelectedRows] = useState<Record<string, SegmentRow>>({});
  const [activeTab, setActiveTab] = useState<TabOptionsType>(TabOptions.UNLABELED);
  const [refreshing, setRefreshing] = useState(false);

  const [labeledEditsMap, setLabeledEditsMap] = useState<SegmentEditsMap>({});
  const [partiallyLabeledEditsMap, setPartiallyLabeledEditsMap] = useState<SegmentEditsMap>({});
  const [unlabeledEditsMap, setUnlabeledEditsMap] = useState<SegmentEditsMap>({});

  const shouldEnableMetricsTableSegmentGroups = useExperimentFlag(
    'enableMetricsTableSegmentGroups'
  ) as boolean;
  const { groupedSegments } = useGroupedSegments({
    company: cid,
    shouldEnableMetricsTableSegmentGroups
  });

  const [dialog, setDialog] = useState<{
    show: boolean;
    title: string;
    body: string;
    intent?: IntentType;
  } | null>(null);

  const [selectedDateRange, setSelectedDateRange] = useState<DateRange | null>(null);

  const shouldEnableDuckDb = useExperimentFlag('enableDuckDb') as boolean;

  const loadDataFromDexie = useCallback(async () => {
    try {
      const [labeledRecords, partiallyLabeledRecords, unlabeledRecords] = await Promise.all([
        db.editRecords
          .where('cid')
          .equals(cid)
          .and(record => record.batchType === TabOptions.LABELED)
          .toArray(),
        db.editRecords
          .where('cid')
          .equals(cid)
          .and(record => record.batchType === TabOptions.PARTIALLY_LABELED)
          .toArray(),
        db.editRecords
          .where('cid')
          .equals(cid)
          .and(record => record.batchType === TabOptions.UNLABELED)
          .toArray()
      ]);

      const labeledMap: SegmentEditsMap = {};
      const partiallyLabeledMap: SegmentEditsMap = {};
      const unlabeledMap: SegmentEditsMap = {};

      labeledRecords.forEach(record => {
        labeledMap[record.campaignKey] = record.rowData;
      });

      partiallyLabeledRecords.forEach(record => {
        partiallyLabeledMap[record.campaignKey] = record.rowData;
      });

      unlabeledRecords.forEach(record => {
        unlabeledMap[record.campaignKey] = record.rowData;
      });

      setLabeledEditsMap(labeledMap);
      setPartiallyLabeledEditsMap(partiallyLabeledMap);
      setUnlabeledEditsMap(unlabeledMap);
    } catch (error) {
      console.error('Error loading data from Dexie:', error);
    }
  }, [cid]);

  useEffect(() => {
    if (!cid) {
      return;
    }

    loadDataFromDexie();
  }, [cid, loadDataFromDexie]);

  const filteredCustomSegments = useMemo(() => {
    if (!customSegments) {
      return [];
    }
    if (!groupedSegments || Object.keys(groupedSegments).length === 0) {
      return [
        ...(customSegments?.filter(segment => !isBillingSegmentName(segment.segmentName)) ?? []),
        ...BILLING_SEGMENT_NAMES.map(segmentName => ({
          segmentId: 'null',
          segmentName,
          values: []
        }))
      ];
    }
    const groupedSegmentObject: CustomSegmentsData[] = [];
    for (const groupName of Object.keys(groupedSegments)) {
      groupedSegmentObject.push({
        segmentId: 'null',
        segmentName: groupName,
        values: groupedSegments[groupName].map(seg => ({
          valueId: seg.segmentId,
          valueName: seg.segmentName
        }))
      });
    }
    const customSegmentsExcludingGrouped =
      customSegments?.filter(
        segment =>
          !Object.values(groupedSegments)
            .flatMap(gs => gs.map(g => g.segmentId))
            .some(segmentId => String(segmentId) === String(segment.segmentId))
      ) ?? [];
    return [
      ...customSegmentsExcludingGrouped.filter(
        segment => !isBillingSegmentName(segment.segmentName)
      ),
      ...groupedSegmentObject,
      ...BILLING_SEGMENT_NAMES.map(segmentName => ({
        segmentId: 'null',
        segmentName,
        values: []
      }))
    ];
  }, [customSegments, groupedSegments]);

  const editsMapByBatch = useMemo(
    () => ({
      [TabOptions.LABELED]: { current: labeledEditsMap, setter: setLabeledEditsMap },
      [TabOptions.UNLABELED]: { current: unlabeledEditsMap, setter: setUnlabeledEditsMap },
      [TabOptions.PARTIALLY_LABELED]: {
        current: partiallyLabeledEditsMap,
        setter: setPartiallyLabeledEditsMap
      }
    }),
    [labeledEditsMap, unlabeledEditsMap, partiallyLabeledEditsMap]
  );

  const updateEdits = useCallback(
    async (updates: { rows: SegmentRow[]; edits: SegmentRowEdit }[], batchType: TabOptionsType) => {
      if (!cid) {
        return;
      }

      const { current: currentEditsMap, setter: setEditsMap } = editsMapByBatch[batchType];
      // Merge the rule updates onto the tab's accumulated pending edits (shared with the
      // automation lambda).
      const filteredEditsMap = applyRuleUpdatesToEditsMap(updates, currentEditsMap);

      for (const [campaignKey, rowData] of Object.entries({
        ...currentEditsMap,
        ...filteredEditsMap
      })) {
        await db.editRecords.add({
          cid,
          campaignKey,
          batchType,
          rowData,
          timestamp: Date.now()
        });
      }

      setEditsMap(filteredEditsMap);
    },
    [cid, editsMapByBatch]
  );

  // NOTE: there's some typing happening that makes replacing updateLabeledEdits/updateUnlabeledEdits functions with updateEdits(updates, batchType) more complicated, so leaving this for now.
  const updateLabeledEdits = useCallback(
    (updates: { rows: SegmentRow[]; edits: SegmentRowEdit }[]) =>
      updateEdits(updates, TabOptions.LABELED),
    [updateEdits]
  );

  const updateUnlabeledEdits = useCallback(
    (updates: { rows: SegmentRow[]; edits: SegmentRowEdit }[]) =>
      updateEdits(updates, TabOptions.UNLABELED),
    [updateEdits]
  );

  const updatePartiallyLabeledEdits = useCallback(
    (updates: { rows: SegmentRow[]; edits: SegmentRowEdit }[]) =>
      updateEdits(updates, TabOptions.PARTIALLY_LABELED),
    [updateEdits]
  );

  const segmentNames = useMemo(
    () => customSegments?.map(segment => segment.segmentName) ?? [],
    [customSegments]
  );

  const completeUnlabeledEditsMap = useMemo(
    () => (customSegments ? filterEditsByCompleteness(unlabeledEditsMap, segmentNames, true) : {}),
    [customSegments, unlabeledEditsMap, segmentNames]
  );

  const completePartiallyLabeledEditsMap = useMemo(
    () =>
      customSegments ? filterEditsByCompleteness(partiallyLabeledEditsMap, segmentNames, true) : {},
    [customSegments, partiallyLabeledEditsMap, segmentNames]
  );

  const incompleteUnlabeledEditsMap = useMemo(
    () => (customSegments ? filterEditsByCompleteness(unlabeledEditsMap, segmentNames, false) : {}),
    [customSegments, unlabeledEditsMap, segmentNames]
  );

  const incompletePartiallyLabeledEditsMap = useMemo(
    () =>
      customSegments
        ? filterEditsByCompleteness(partiallyLabeledEditsMap, segmentNames, false)
        : {},
    [customSegments, partiallyLabeledEditsMap, segmentNames]
  );

  const deleteSegmentEdit = useCallback(
    async (campaign_keys: string[], segmentNamesToDelete: string[], batchType: TabOptionsType) => {
      if (!cid || !campaign_keys || !segmentNamesToDelete) {
        return;
      }

      const { current: currentEditsMap, setter: setEditsMap } = editsMapByBatch[batchType];
      const newEditsMap = { ...currentEditsMap };

      campaign_keys.forEach(campaign_key => {
        for (const segmentName of segmentNamesToDelete) {
          const updatedRow = newEditsMap[campaign_key];
          if (updatedRow) {
            const updatedRowCopy = JSON.parse(JSON.stringify(updatedRow));
            const updatedRowEdits = updatedRowCopy.edits ?? {};
            delete updatedRowEdits[segmentName];

            if (Object.keys(updatedRowEdits).length === 0) {
              delete newEditsMap[campaign_key];
            } else {
              newEditsMap[campaign_key] = updatedRowCopy;
            }
          }
        }
      });

      setEditsMap(newEditsMap);

      await db.editRecords
        .where('cid')
        .equals(cid)
        .and(record => record.batchType === batchType)
        .delete();

      const editRecords = Object.entries(newEditsMap).map(([campaignKey, rowData]) => ({
        cid,
        campaignKey,
        batchType,
        rowData,
        timestamp: Date.now()
      }));

      if (editRecords.length > 0) {
        await db.editRecords.bulkAdd(editRecords);
      }
    },
    [cid, editsMapByBatch]
  );

  const resetEdits = useCallback(
    async (batchType: TabOptionsType) => {
      if (!cid) {
        return;
      }

      await db.editRecords
        .where('cid')
        .equals(cid)
        .and(record => record.batchType === batchType)
        .delete();

      editsMapByBatch[batchType].setter({});
    },
    [cid, editsMapByBatch]
  );

  const filterBarOptions = useMemo(() => {
    const uniqueSegmentOptions =
      customSegments?.map(segment => ({
        name: segment.segmentName,
        label: segment.segmentName
      })) ?? [];

    const options = [
      ...uniqueSegmentOptions,
      { name: 'account_id', label: 'Account ID' },
      { name: 'account_name', label: 'Account Name' },
      { name: 'campaign_id', label: 'Campaign ID' },
      { name: 'campaign_name', label: 'Campaign' }
    ];

    if (dataGranularity === 'ad_group' || dataGranularity === 'ad') {
      options.push({ name: 'ad_group_id', label: 'Ad Group ID' });
      options.push({ name: 'ad_group_name', label: 'Ad Group' });
    }

    if (dataGranularity === 'ad') {
      options.push({ name: 'ad_id', label: 'Ad ID' });
      options.push({ name: 'ad_name', label: 'Ad' });
    }
    return options;
  }, [customSegments, dataGranularity]);

  // Billing segment values are stored as an array of date-ranged entries.
  // Give each billing option the same date-aware display value the table cell shows
  // ("Mixed"/"Unlabeled"/the value); the parser uses this getter for both the option list and the
  // match. This augments the UI options only — the base filterBarOptions still feed rule application.
  const filterBarOptionsWithBillingValues = useMemo<Option[]>(
    () =>
      filterBarOptions.map(option => {
        const { name } = option;
        if (!name || !isBillingSegmentName(name)) {
          return option;
        }
        return {
          ...option,
          optionsGetter: (line: Record<string, unknown>) => {
            const value = resolveBillingSegmentValue(
              line[name] as RawBillingSegmentMapEntry[],
              selectedDateRange,
              name
            );
            return value == null ? [] : [value];
          }
        };
      }),
    [filterBarOptions, selectedDateRange]
  );

  const unlabeledData = useMemo(
    () => data?.filter(row => !(row.pg_seg_id || row.ingested_seg_id)),
    [data]
  );

  const labeledData = useMemo(
    () =>
      data?.filter(row => {
        const hasSegmentId = row.pg_seg_id || row.ingested_seg_id;
        const hasAllSegmentValues = !customSegments?.some(
          segment => row[segment.segmentName] === null
        );

        return hasSegmentId && hasAllSegmentValues;
      }),
    [customSegments, data]
  );

  const partiallyLabeledData = useMemo(() => {
    if (!data) {
      return data;
    }
    const labeledSet = new Set(labeledData ?? []);
    const unlabeledSet = new Set(unlabeledData ?? []);
    return data.filter(row => !labeledSet.has(row) && !unlabeledSet.has(row));
  }, [data, labeledData, unlabeledData]);

  const dataToUse = useMemo(() => {
    if (activeTab === TabOptions.UNLABELED) {
      return {
        data: unlabeledData,
        editsMap: unlabeledEditsMap,
        updateEdits: updateUnlabeledEdits
      };
    }
    if (activeTab === TabOptions.LABELED) {
      return { data: labeledData, editsMap: labeledEditsMap, updateEdits: updateLabeledEdits };
    }
    return {
      data: partiallyLabeledData,
      editsMap: partiallyLabeledEditsMap,
      updateEdits: updatePartiallyLabeledEdits
    };
  }, [
    labeledData,
    labeledEditsMap,
    activeTab,
    unlabeledData,
    unlabeledEditsMap,
    partiallyLabeledData,
    partiallyLabeledEditsMap,
    updateLabeledEdits,
    updatePartiallyLabeledEdits,
    updateUnlabeledEdits
  ]);

  const [filter, setFilter] = useStateFunction<(line: SegmentRow) => boolean>(() => true);

  const filteredData = useMemo(() => dataToUse.data?.filter(filter) || [], [filter, dataToUse]);

  const [showPendingChanges, setShowPendingChanges] = useState<boolean>(false);
  const hasPendingChanges = (batchType: TabOptionsType) => {
    if (batchType === TabOptions.LABELED) {
      return Object.keys(labeledEditsMap).length > 0;
    }
    if (batchType === TabOptions.UNLABELED) {
      return Object.keys(unlabeledEditsMap).length > 0;
    }
    return Object.keys(partiallyLabeledEditsMap).length > 0;
  };

  const pendingChangesCount = (batchType: TabOptionsType) => {
    if (batchType === TabOptions.LABELED) {
      return Object.keys(labeledEditsMap).length;
    }
    if (batchType === TabOptions.UNLABELED) {
      return Object.keys(unlabeledEditsMap).length;
    }
    return Object.keys(partiallyLabeledEditsMap).length;
  };

  const clearChanges = async (batchType: TabOptionsType) => {
    if (cid) {
      try {
        await resetEdits(batchType);
      } catch (e) {
        setError({
          message: `Failed clearing edits from DexieDB for ${cid} and batch type ${batchType}. Error: ${e.message}`,
          reportError: e
        });
      }
    }

    setShowPendingChanges(false);
  };

  const [showConfirmDiscardDialog, setShowConfirmDiscardDialog] = useState(false);

  const [saving, setSaving] = useState(false);
  const userEmail = useAppSelector(UserRedux.emailSelector);

  const startLambdaJob = useCallback(
    async (batchType: TabOptionsType, timestamp: number) => {
      const body = {
        company: cid,
        user: userEmail,
        batchType: batchType.toLowerCase().replace(' ', '_'),
        timestamp,
        useDuckDb: shouldEnableDuckDb
      };

      await SegmentationMappingLambdaFetch('/updateManualSegmentationMaps', {
        method: 'POST',
        body
      });
    },
    [cid, userEmail, shouldEnableDuckDb]
  );

  const uploadUpdatedRowsToS3 = async (batchType: TabOptionsType) => {
    if (!customSegments) {
      return;
    }

    setSaving(true);
    try {
      const timestamp = Date.now();
      const editsMap = editsMapByBatch[batchType].current ?? {};
      const updatedRows = buildUpdatedRowsPayload(editsMap, customSegments);
      const updatedBillingRows = buildUpdatedBillingRowsPayload(editsMap);

      if (updatedBillingRows.length > 0) {
        const compressedBillingData = gzip(JSON.stringify(updatedBillingRows));
        const base64BillingData = btoa(
          Array.from(compressedBillingData, (byte: number) => String.fromCharCode(byte)).join('')
        );

        await S3Put(
          S3_BUCKET_LOCATION,
          `billing-segments/${cid}/${batchType.toLowerCase().replace(' ', '_')}/${timestamp}/payload.json.gz`,
          base64BillingData,
          {
            bufferEncoding: 'base64',
            contentType: 'application/gzip'
          }
        );

        await SegmentationMappingLambdaFetch('/updateBillingSegments', {
          method: 'POST',
          body: {
            company: cid,
            batchType: batchType.toLowerCase().replace(' ', '_'),
            timestamp
          }
        });
      }
      if (updatedRows.length > 0) {
        const jsonString = JSON.stringify(updatedRows);

        const compressedData = gzip(jsonString);
        const base64Data = btoa(
          Array.from(compressedData, (byte: number) => String.fromCharCode(byte)).join('')
        );

        await S3Put(
          S3_BUCKET_LOCATION,
          `jp-test/${cid}/${batchType.toLowerCase().replace(' ', '_')}/${timestamp}/payload.json.gz`,
          base64Data,
          {
            bufferEncoding: 'base64',
            contentType: 'application/gzip'
          }
        );

        await startLambdaJob(batchType, timestamp);
      }
    } catch (e) {
      // Edits stay in Dexie, so the user can save again. A repeated billing save rewrites the same rows.
      setError({
        title: 'Save failed',
        message: `The save of your changes from the ${batchType.toLowerCase()} tab did not finish. Your edits are kept, so you can try again. Error: ${e.message}`,
        reportError: e
      });
      return;
    } finally {
      setSaving(false);
    }

    setDialog({
      show: true,
      title: 'Segmentation Labeling Saving In Progress',
      body: `Your changes from the ${batchType.toLowerCase()} tab are being saved. You will recieve an email when the save is complete. You may close this window at any time.`,
      intent: Intent.SUCCESS
    });

    try {
      await fetchRawData();
      setSelectedRows({});

      await resetEdits(batchType);
      await loadDataFromDexie();
    } catch (e) {
      // fetchRawData shows its own error modal, so only the Dexie reset and reload reach this catch.
      setError({
        title: 'Local edits not cleared',
        message: `Your changes were sent for saving, but the page could not clear your local edits. Reload the page. Error: ${e.message}`,
        reportError: e
      });
    }
  };

  const refreshCrossChannel = useCallback(() => {
    (async () => {
      try {
        setRefreshing(true);
        Mixpanel.track(MxE.RefreshCcViews);
        const res = await CrossChannelLambdaFetch('/refreshCrossChannelViews', {
          method: 'POST',
          body: { company: cid }
        });
        const resJson = await awaitJSON(res);
        const { message, status } = resJson;
        setRefreshing(false);
        setError({
          intent: status === 'IN_PROGRESS' ? Intent.WARNING : Intent.SUCCESS,
          title: status === 'IN_PROGRESS' ? 'Publish already in progress' : 'Publish started',
          message: <div>{message}</div>
        });
      } catch (e) {
        setRefreshing(false);
        setError({
          message: `Failed to publish to dashboards for ${cid}. Error: ${e.message}`,
          reportError: e
        });
      }
    })();
  }, [cid, setError]);

  const granularityPrettyName = useMemo(() => {
    switch (dataGranularity) {
      case 'campaign':
        return 'Campaign';
      case 'ad_group':
        return 'Ad Group';
      case 'ad':
        return 'Ad';
      default:
        return '';
    }
  }, [dataGranularity]);

  const [showRuleModal, setShowRuleModal] = useState<boolean>(false);
  const [mixedBillingSegmentRow, setMixedBillingSegmentRow] = useState<{
    segmentName: string;
    entries: Record<string, string | null>[];
    committedEntries?: RawBillingSegmentMapEntry[];
  } | null>(null);

  // Context for the shared rule-application pipeline (filter-bar options + segment/value id→name
  // maps). customSegments already includes the synthetic billing segments (appended upstream).
  const ruleContext = useMemo(
    () => buildSegmentEditContext(customSegments ?? [], dataGranularity, cid),
    [customSegments, dataGranularity, cid]
  );

  const applyRules = useCallback(
    async (
      selectedRules: (ModifiedSegmentationMappingLabelRule | ModifiedBillingMappingLabelRule)[]
    ) => {
      try {
        const updates: { rows: SegmentRow[]; edits: SegmentRowEdit }[] = prepareUpdatesForRules(
          Object.values(selectedRows).length > 0
            ? Object.values(selectedRows)
            : dataToUse.data || [],
          selectedRules,
          ruleContext
        );

        await dataToUse.updateEdits(updates);

        setShowRuleModal(false);
      } catch (e) {
        setError({ message: e.message, reportError: e });
      }
    },
    [dataToUse, selectedRows, ruleContext, setError]
  );

  const handleDownloadCsv = () => {
    const { data: sourceData, editsMap } = dataToUse;
    const date = new Date();
    // Extract all segment names from all groups to identify boolean segment columns
    const allGroupedSegmentNames = new Set<string>();
    const groupNames: string[] = [];
    if (groupedSegments && typeof groupedSegments === 'object' && !Array.isArray(groupedSegments)) {
      Object.keys(groupedSegments).forEach(groupName => {
        groupNames.push(groupName);
        const segments = groupedSegments[groupName];
        if (Array.isArray(segments)) {
          segments.forEach(segment => {
            if (segment.segmentName) {
              allGroupedSegmentNames.add(segment.segmentName);
            }
          });
        }
      });
    }
    const csvData = (sourceData ?? [])
      .map(row => editsMap[row.campaign_key] ?? row)
      .map(row => {
        const cleanedRow: Record<string, unknown> = {
          ...R.mapObjIndexed(v => (v === null ? '' : v), row),
          segmentation_id: row.ingested_seg_id ?? row.pg_seg_id
        };
        const customSegmentNames = customSegments?.map(cs => cs.segmentName) ?? [];

        delete cleanedRow.ingested_seg_id;
        delete cleanedRow.pg_seg_id;
        delete cleanedRow.platform_id;
        delete cleanedRow.channel_id;
        delete cleanedRow.edits;
        delete cleanedRow.billing_seg_map;

        BILLING_SEGMENT_NAMES.forEach(billingName => {
          const entries = cleanedRow[billingName];
          if (Array.isArray(entries)) {
            cleanedRow[billingName] =
              entries.length > 0 ? formatBillingSegmentEntries(entries, billingName) : '';
          }
        });

        const groupColumns: Record<string, string> = {};
        if (
          groupedSegments &&
          typeof groupedSegments === 'object' &&
          !Array.isArray(groupedSegments)
        ) {
          Object.keys(groupedSegments).forEach(groupName => {
            const segments = groupedSegments[groupName];
            if (Array.isArray(segments)) {
              const groupValues: string[] = [];
              segments.forEach(({ segmentName }: GroupedSegment) => {
                if (segmentName && cleanedRow[segmentName] === 'Yes') {
                  groupValues.push(segmentName);
                }
                if (allGroupedSegmentNames.has(segmentName)) {
                  delete cleanedRow[segmentName];
                }
              });
              groupColumns[groupName] = groupValues.join(', ');
            }
          });
        }

        Object.keys(groupColumns).forEach(groupName => {
          cleanedRow[groupName] = groupColumns[groupName];
        });

        // Sort the columns: platform, channel, custom segments, group columns, then all other attributes
        const customFields = Object.keys(cleanedRow)
          .filter(
            key =>
              customSegmentNames.includes(key) &&
              key !== 'Platform' &&
              key !== 'Channel' &&
              !groupNames.includes(key)
          )
          .sort();

        const otherFields = Object.keys(cleanedRow)
          .filter(
            key =>
              !customSegmentNames.includes(key) &&
              !groupNames.includes(key) &&
              key !== 'platform' &&
              key !== 'channel' &&
              key !== 'segmentation_id'
          )
          .sort();

        const sortedKeys = [
          'Platform',
          'Channel',
          ...customFields,
          ...groupNames.sort(),
          ...otherFields
        ];
        const sortedRow: Record<string, unknown> = {};
        sortedKeys.forEach(key => {
          sortedRow[key] = cleanedRow[key];
        });
        return sortedRow;
      });
    downloadSegmentationDataToCSV(
      csvData,
      `SegmentationLabeling ${cid} ${activeTab.toLowerCase()} ${date.toUTCString()}`
    );
  };

  const hasLabelingRules = labelingRules.length > 0 || billingLabelingRules.length > 0;

  // The pending-changes panel only deletes individual segment edits; route each
  // delete to its own bucket. (PendingSegmentationEdits never dispatches
  // UPDATE_SEGMENT_EDIT or RESET, so those are intentionally not handled here.)
  const dispatchDeleteFromBucket = (bucket: TabOptionsType) => (action: EditsMapAction) => {
    if (action.type === 'DELETE_SEGMENT_EDIT') {
      deleteSegmentEdit(action.campaign_keys || [], action.segmentNames || [], bucket);
    }
  };
  const dispatchUnlabeled = dispatchDeleteFromBucket(TabOptions.UNLABELED);
  const dispatchPartiallyLabeled = dispatchDeleteFromBucket(TabOptions.PARTIALLY_LABELED);
  const dispatchLabeled = dispatchDeleteFromBucket(TabOptions.LABELED);

  const granularityLabel = dataGranularity.replace('_', ' ');
  // Only show pending changes for the tab the user is currently on.
  let segmentEditedRowGroups: { label: string; rows: PendingSegmentRow[] }[];
  if (activeTab === TabOptions.UNLABELED) {
    segmentEditedRowGroups = [
      {
        label: `Partially labeled ${granularityLabel}s`,
        rows: formatSegmentRows(incompleteUnlabeledEditsMap, dispatchUnlabeled, false)
      },
      {
        label: `Fully labeled ${granularityLabel}s`,
        rows: formatSegmentRows(completeUnlabeledEditsMap, dispatchUnlabeled, false)
      }
    ];
  } else if (activeTab === TabOptions.PARTIALLY_LABELED) {
    segmentEditedRowGroups = [
      {
        label: `Partially labeled ${granularityLabel}s`,
        rows: formatSegmentRows(incompletePartiallyLabeledEditsMap, dispatchPartiallyLabeled, false)
      },
      {
        label: `Fully labeled ${granularityLabel}s`,
        rows: formatSegmentRows(completePartiallyLabeledEditsMap, dispatchPartiallyLabeled, false)
      }
    ];
  } else {
    segmentEditedRowGroups = [
      {
        label: `Segment updates for ${granularityLabel}s`,
        rows: formatSegmentRows(labeledEditsMap, dispatchLabeled, true)
      }
    ];
  }
  const segmentOriginalData = Object.fromEntries(
    segmentEditedRowGroups.flatMap(group => group.rows).map(row => [row.id, row])
  );

  return (
    <div className='segmentation-labeling'>
      <Nav
        activeKey={activeTab}
        onSelect={value => {
          const valueTab = value as TabOptionsType;

          if (!valueTab || valueTab === activeTab) {
            return;
          }

          setActiveTab(valueTab);
          setSelectedRows({});
        }}
      >
        <Nav.Item key={TabOptions.UNLABELED}>
          <Nav.Link eventKey={TabOptions.UNLABELED}>
            <div className='nav-button-container'>
              {TabOptions.UNLABELED}
              {unlabeledData && unlabeledData.length > 0 ? (
                <div className='warning-icon-container'>
                  {unlabeledData?.length ?? 0}
                  <MdNewReleases />
                </div>
              ) : (
                <div className='success-icon-container'>
                  <MdCheck />
                </div>
              )}
            </div>
          </Nav.Link>
        </Nav.Item>
        <Nav.Item key={TabOptions.PARTIALLY_LABELED}>
          <Nav.Link eventKey={TabOptions.PARTIALLY_LABELED}>
            <div className='nav-button-container'>
              {TabOptions.PARTIALLY_LABELED}
              {partiallyLabeledData && partiallyLabeledData.length > 0 ? (
                <div className='warning-icon-container'>
                  {partiallyLabeledData?.length ?? 0}
                  <MdNewReleases />
                </div>
              ) : (
                <div className='success-icon-container'>
                  <MdCheck />
                </div>
              )}
            </div>
          </Nav.Link>
        </Nav.Item>
        <Nav.Item key={TabOptions.LABELED}>
          <Nav.Link eventKey={TabOptions.LABELED}>
            <div className='nav-button-container'>
              {TabOptions.LABELED}
              <div className='success-icon-container'>
                <MdCheck />
              </div>
            </div>
          </Nav.Link>
        </Nav.Item>
      </Nav>
      <div className='segmentation-labeling-body'>
        {dataToUse.data ? (
          <>
            <div className='filter-bar-container'>
              <FilterBar
                size={Size.LG}
                options={filterBarOptionsWithBillingValues}
                lines={dataToUse.data || []}
                onFilter={setFilter}
                hasAdvanced
              />
              <div className='pending-changes-controls'>
                <OverlayTrigger
                  placement={Placement.TOP_END}
                  delay={500}
                  overlay={
                    <Tooltip>
                      {hasLabelingRules &&
                        'Select a list of rules to apply to unlabeled segments. This will overwrite any existing changes currently applied to the data.'}
                      {!hasLabelingRules &&
                        'No labeling rules available. Please create a rule on the Labeling Rules tab.'}
                    </Tooltip>
                  }
                >
                  <Button
                    size={Size.LG}
                    appearance={Appearance.OUTLINED}
                    leadingElement={<BiBookmarkHeart color={Brand60} />}
                    disabled={!hasLabelingRules || refreshing}
                    onClick={() => setShowRuleModal(true)}
                  >
                    Apply Labeling Rules
                  </Button>
                </OverlayTrigger>
                <SelectLabelingRulesModal
                  show={showRuleModal}
                  setShow={setShowRuleModal}
                  labelingRules={labelingRules}
                  billingRules={billingLabelingRules}
                  customSegments={customSegments}
                  onRuleApply={applyRules}
                />
                <OverlayTrigger
                  placement={Placement.TOP_END}
                  delay={500}
                  overlay={
                    <Tooltip>
                      {hasPendingChanges(activeTab)
                        ? 'Please save any labeling changes before publishing to other parts of the app.'
                        : 'Publish the latest mapping changes to dashboards now.\nChanges will auto-update by the next day if not manually published.'}
                    </Tooltip>
                  }
                >
                  <Button
                    size={Size.LG}
                    appearance={Appearance.OUTLINED}
                    leadingElement={<MdSend color={Brand60} />}
                    loading={refreshing}
                    onClick={() => refreshCrossChannel()}
                    disabled={hasPendingChanges(activeTab)}
                  >
                    Publish to dashboards
                  </Button>
                </OverlayTrigger>
                <IconButton onClick={handleDownloadCsv}>
                  <MdOutlineFileDownload />
                </IconButton>
              </div>
            </div>
            {mixedBillingSegmentRow && (
              <MixedBillingSegmentModal
                data={mixedBillingSegmentRow}
                show={Boolean(mixedBillingSegmentRow)}
                setShow={show => {
                  if (!show) {
                    setMixedBillingSegmentRow(null);
                  }
                }}
              />
            )}
            <SegmentationLabelingTable
              data={filteredData}
              customSegments={customSegments}
              filteredCustomSegments={filteredCustomSegments}
              editsMap={dataToUse.editsMap}
              selectedRows={selectedRows}
              dispatchEditsMap={action => {
                if (action.type === 'UPDATE_SEGMENT_EDIT') {
                  dataToUse.updateEdits(action.updates || []);
                } else if (action.type === 'DELETE_SEGMENT_EDIT') {
                  deleteSegmentEdit(
                    action.campaign_keys || [],
                    action.segmentNames || [],
                    activeTab
                  );
                } else if (action.type === 'RESET') {
                  resetEdits(activeTab);
                }
              }}
              setSelectedRows={setSelectedRows}
              dataGranularity={dataGranularity}
              groupedSegments={groupedSegments}
              selectedDateRange={selectedDateRange}
              setSelectedDateRange={setSelectedDateRange}
              setMixedBillingSegmentRow={setMixedBillingSegmentRow}
              showLastModified={activeTab !== TabOptions.UNLABELED}
            />
          </>
        ) : (
          <FullPageSpinner />
        )}
        <div className='segmentation-labeling-controls'>
          <span className='unlabeled-counter'>
            <span className={cn({ 'unsaved-count': hasPendingChanges(activeTab) })}>
              {formatNumber(
                (labeledData?.length ?? 0) +
                  (activeTab === TabOptions.LABELED ? 0 : pendingChangesCount(activeTab))
              )}
            </span>
            /
            {formatNumber(
              (unlabeledData?.length ?? 0) +
                (labeledData?.length ?? 0) +
                (partiallyLabeledData?.length ?? 0)
            )}{' '}
            {granularityPrettyName}s Labeled
          </span>
          <PendingChangesControls
            hasPendingChanges={hasPendingChanges(activeTab)}
            setShowPendingChanges={setShowPendingChanges}
            clearAllChanges={() => setShowConfirmDiscardDialog(true)}
            saveChanges={() => uploadUpdatedRowsToS3(activeTab)}
            isSaving={saving}
          />
        </div>
        {showPendingChanges && hasPendingChanges(activeTab) && (
          <PendingChangesPanel
            headers={buildSegmentHeaders(segmentNames, groupedSegments ?? {})}
            originalData={segmentOriginalData}
            pendingChanges={{}}
            editedRowGroups={segmentEditedRowGroups}
            showPendingChanges={showPendingChanges}
            setShowPendingChanges={setShowPendingChanges}
          />
        )}
      </div>
      {dialog && (
        <AlertDialog
          show={dialog.show}
          onHide={() => setDialog(null)}
          icon={null}
          title={dialog.title}
          body={dialog.body}
          primaryAction={{ label: 'Close' }}
          intent={dialog.intent}
        />
      )}
      <AlertDialog
        show={showConfirmDiscardDialog}
        icon={<MdDelete color={Warning500} size={75} />}
        intent={Intent.WARNING}
        title='Discard changes?'
        body="Any edits you've made won't be saved and you will have to start over."
        primaryAction={{ onClick: () => clearChanges(activeTab), label: 'Discard' }}
        secondaryAction={{ onClick: () => setShowConfirmDiscardDialog(false) }}
        onHide={() => setShowConfirmDiscardDialog(false)}
      />
    </div>
  );
};
